import { useEffect, useState } from "react";
import { logger } from "../../utils/logger";
import { useSelector } from "react-redux";
import { Box, Stack, Typography, Link as MuiLink, CircularProgress } from "@mui/material";
import { ReactComponent as Logo } from "../../assets/svg/logo.svg";
import { Link } from "react-router-dom";

export const useFooter = () => {
  const [footer, setFooter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const language = useSelector((state) => state.language.activeLanguage);

  useEffect(() => {
    const loadFooter = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`./config/${language}/footer.json`);
        if (!response.ok) {
          throw new Error("Failed to load footer config");
        }
        const data = await response.json();
        setFooter(data);
      } catch (e) {
        logger.error("Error loading footer config:", e.message);
        setError(e.message);
        setFooter(null);
      } finally {
        setLoading(false);
      }
    };
    loadFooter();
  }, [language]);

  return { footer, loading, error };
};

const Footer = () => {
  const { footer, loading, error } = useFooter();

  if (loading || !footer) {
    return (
      <Box className="footer-loading">
        <CircularProgress size={24} />
      </Box>
    );
  }

  const { isVisible } = footer;

  if (!isVisible) {
    return null;
  }

  const { companyInfo, sections, bottom } = footer;

  return (
    <Box component="footer" className="footer">
      <Box className="footer-container">
        <Stack className="footer-content">
          <Box className="footer-company">
            <Logo height={companyInfo.logoHeight} className="company-logo" />
            <Typography className="company-description">
              {companyInfo.description}
            </Typography>
          </Box>

          <Stack className="footer-sections">
            {sections.map((section) => (
              <Box key={section.id}>
                <Typography className="footer-section-title">
                  {section.title}
                </Typography>
                {section.type === "navigation" || section.type === "static" ? (
                  <Stack className="footer-links">
                    {section.links.map((link, linkIndex) => (
                      link.external ? (
                        <MuiLink
                          key={linkIndex}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="footer-link"
                        >
                          {link.text}
                        </MuiLink>
                      ) : (
                        <Link
                          key={linkIndex}
                          to={link.url}
                          className="footer-link"
                        >
                          {link.text}
                        </Link>
                      )
                    ))}
                  </Stack>
                ) : section.type === "contact" ? (
                  <Stack className="contact-info">
                    {section.info.map((contactInfo, infoIndex) => (
                      <Typography key={infoIndex} className="contact-item">
                        <Box component="span" className="contact-label">
                          {contactInfo.label}:
                        </Box>{" "}
                        {contactInfo.value}
                      </Typography>
                    ))}
                  </Stack>
                ) : null}
              </Box>
            ))}
          </Stack>
        </Stack>

        <Box className="footer-bottom">
          <Typography className="copyright">
            © {new Date().getFullYear()} {bottom.copyright}
          </Typography>
          <Stack className="legal-links">
            {bottom.legalLinks.map((link, index) => (
              <Link key={index} to={link.url} className="legal-link">
                {link.text}
              </Link>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;